import React from 'react';
import type { ColumnProps } from '#/common/components/DataGrid';
import { withChip } from '#/common/utils/wrappers';
import type { SyncHistoryItem } from '#/graphql/types';
import {
  syncHistoryStatusColors,
  syncStatus,
  syncType,
  syncTypeList,
  syncSourceList,
  syncStatusList,
} from '#/modules/admin/syncHistory/utils/constants';
import { getTenantList } from '#/graphql/queries';
import { gql } from '@apollo/client';

const ALL_ORGANIZATIONS_LABEL = 'All Organizations';

const renderOrganization = (e: any) => <span>{e.value ?? ALL_ORGANIZATIONS_LABEL}</span>;

const renderType = (e: any) => (
  <span>{syncType[e.value as keyof typeof syncType]?.label ?? (e.value as string)}</span>
);

const renderSyncedBy = (e: any) => <span>{e.value ?? 'System'}</span>;

const renderStatus = (e: any) => {
  const key = e.value as keyof typeof syncStatus;
  const colors = syncHistoryStatusColors[key];
  const label = syncStatus[key]?.label ?? key;
  return withChip(label, 'normal', colors);
};

const formatDateTime = (value?: string | null): string => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
};

const columns: ColumnProps<SyncHistoryItem>[] = [
  {
    dataField: 'id',
    caption: 'ID',
    visible: false,
    showInColumnChooser: false,
    dataType: 'string',
  },
  {
    dataField: 'displayNumber',
    caption: 'Number',
    dataType: 'string',
    visible: true,
    fixed: true,
    openOnClick: true,
    path: (row) => `/admin/sync-history/${row.id}/details`,
    sortIndex: 0,
    sortOrder: 'desc',
  },
  {
    dataField: 'startedAt',
    caption: 'Started At',
    dataType: 'datetime',
    visible: true,
    calcDisplayValue: (row) => formatDateTime(row.startedAt),
  },
  { dataField: 'syncedCount', caption: 'Synced Items', dataType: 'number', visible: true },
  {
    dataField: 'source',
    caption: 'Source',
    dataType: 'string',
    visible: true,
    lookup: {
      nullable: true,
      dataSource: syncSourceList,
      displayExpr: 'label',
      valueExpr: 'key',
    },
  },
  {
    dataField: 'organization.name',
    caption: 'Organization',
    dataType: 'string',
    visible: true,
    cellRender: renderOrganization,
    headerFilter: {
      getItemList: gql(getTenantList),
      displayExpr: 'name',
      nullLabel: ALL_ORGANIZATIONS_LABEL,
    },
  },
  {
    dataField: 'type',
    caption: 'Type',
    dataType: 'string',
    visible: true,
    cellRender: renderType,
    lookup: {
      nullable: true,
      dataSource: syncTypeList,
      displayExpr: 'label',
      valueExpr: 'key',
    },
  },
  {
    dataField: 'status',
    caption: 'Status',
    dataType: 'string',
    visible: true,
    cellRender: renderStatus,
    lookup: {
      nullable: true,
      dataSource: syncStatusList,
      displayExpr: 'label',
      valueExpr: 'key',
    },
  },
  {
    dataField: 'syncedByUser.name',
    caption: 'Synced By',
    dataType: 'string',
    visible: true,
    cellRender: renderSyncedBy,
  },
  {
    dataField: 'createdAt',
    caption: 'Created At',
    dataType: 'datetime',
    visible: false,
    calcDisplayValue: (row) => formatDateTime(row.createdAt),
  },
  {
    dataField: 'finishedAt',
    caption: 'Finished At',
    dataType: 'datetime',
    visible: false,
    calcDisplayValue: (row) => formatDateTime(row.finishedAt),
  },
  { dataField: 'result', caption: 'Result', dataType: 'string', visible: false },
];

export default columns;
